import { ColumnProps, DataTable } from "components/Table";
import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { PageContainer } from 'components/PageContainer'
import { Toolbar } from "components/Toolbar";
import { toast } from 'react-toastify';
import { costManagementService } from 'services/costManagementService';
import { MainContainer } from './styles';
import { IWaybill } from 'interfaces/IWaybill';
import { FormTableLine } from './Components/FormTable';
import { dateToday } from "utils/dateToday";
import { ActiveFilters } from "components/ActiveFilters";
import { CustomPagination } from "components/CustomPagination";
import dayjs from "dayjs";
import saveAs from "file-saver";
import PlanilhaIcon from "assets/planilhaIcon.svg";
import { ConfirmChangesPopup, IPendingChange } from "../Components/ConfirmChangesPopup";

export interface ILastMile {
  id: number;
  waybill: IWaybill;
  defaultCost: number;
  costAdjustment: number;
  leaderCommission: number;
  additionalDelivery: number;
  additionalDeliveryTotal?: number;
  additionalDeliveryTotalCalculation?: string;
  unloadingCost?: number;
}

const defaultColumns: ColumnProps[] = [
  {
    name: "Motorista",
    align: "left",
    minWidth: "120px",
  },
  {
    name: "Placa",
    align: "center",
    minWidth: "80px",
  },
  {
    name: "Data de Entrega",
    align: "center",
    minWidth: "110px",
  },
  {
    name: "Rota",
    align: "center",
    minWidth: "70px",
  },
  {
    name: "CD",
    align: "center",
    minWidth: "100px",
  },
  {
    name: "Comissão do Líder",
    align: "center",
    minWidth: "130px",
  },
  {
    name: "Adic. Entrega",
    align: "center",
    minWidth: "140px",
  },
  {
    name: "Valor da Rota",
    align: "center",
    minWidth: "120px",
  },
  {
    name: "Val. Descarregamento",
    align: "center",
    minWidth: "140px",
  },
  {
    name: "Ajuste",
    align: "center",
    minWidth: "100px",
  },
  {
    name: "Total",
    align: "center",
    minWidth: "100px",
  },
  {
    name: "",
    minWidth: "40px",
  }
]

export const LastMile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [originalData, setOriginalData] = useState<ILastMile[]>([]);
  const [editedData, setEditedData] = useState<ILastMile[]>([]);
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);

  // Map para armazenar alterações pendentes que persistem entre mudanças de filtros/paginação
  // Chave: id do item, Valor: dados originais e editados
  const pendingChangesRef = useRef<Map<number, { original: ILastMile; edited: ILastMile }>>(new Map());

  // Função para ler os search params da URL
  const [params, setParams] = useState(() => {
    const transshipPointIds = searchParams.getAll('transshipPointIds').map(id => Number(id)).filter(id => !isNaN(id));
    const teamIds = searchParams.getAll('teamIds').map(id => Number(id)).filter(id => !isNaN(id));
    const startDate = searchParams.get('startDate') || dateToday();
    const endDate = searchParams.get('endDate') || dateToday();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 15;
    const search = searchParams.get('search') || '';

    return {
      transshipPointIds: transshipPointIds.length > 0 ? transshipPointIds : undefined,
      teamIds: teamIds.length > 0 ? teamIds : undefined,
      startDate,
      endDate,
      page,
      pageSize,
      search
    };
  });

  const [paginationData, setPaginationData] = useState<{
    totalPages: number;
    pageIndex: number;
  }>({
    totalPages: 1,
    pageIndex: 1
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      const data = await costManagementService.getLastMileCost(params);

      setOriginalData(data.results);
      
      // Mesclar dados da API com alterações pendentes
      const mergedData = data.results.map((item: ILastMile) => {
        const pending = pendingChangesRef.current.get(item.id);
        if (pending) {
          // Manter as alterações pendentes
          return { ...item, ...pending.edited, id: item.id };
        }
        return item;
      });
      
      setEditedData(mergedData);
      setPaginationData({
        totalPages: data.totalPages,
        pageIndex: data.pageIndex
      });
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao buscar dados no sistema";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditField = (id: number, field: keyof ILastMile, value: any) => {
    // Atualizar o estado local
    setEditedData(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));

    // Salvar alteração no Map persistente
    const original = originalData.find(item => item.id === id);
    if (original) {
      const currentPending = pendingChangesRef.current.get(id);
      const currentEdited = currentPending?.edited || { ...original };
      
      pendingChangesRef.current.set(id, {
        original,
        edited: { ...currentEdited, [field]: value }
      });

      // Verificar se voltou ao valor original
      const updatedEdited = { ...currentEdited, [field]: value };
      if (
        updatedEdited.defaultCost === original.defaultCost &&
        updatedEdited.costAdjustment === original.costAdjustment &&
        updatedEdited.unloadingCost === original.unloadingCost &&
        updatedEdited.leaderCommission === original.leaderCommission
      ) {
        pendingChangesRef.current.delete(id);
      }
    }
  };

  const handleRollback = (id: number) => {
    const original = originalData.find(item => item.id === id);
    if (!original) return;
    
    setEditedData(prev => prev.map(item =>
      item.id === id ? { ...original } : item
    ));

    // Remover do Map de alterações pendentes
    pendingChangesRef.current.delete(id);
  };

  const isRowEdited = (id: number) => {
    // Verificar primeiro no Map de pendentes (para itens que não estão na página atual)
    if (pendingChangesRef.current.has(id)) {
      return true;
    }
    
    const orig = originalData.find(item => item.id === id);
    const edit = editedData.find(item => item.id === id);
    if (!orig || !edit) return false;
    return (
      orig.defaultCost !== edit.defaultCost ||
      orig.costAdjustment !== edit.costAdjustment ||
      orig.unloadingCost !== edit.unloadingCost ||
      orig.leaderCommission !== edit.leaderCommission
    );
  };

  const getPendingChangesCount = () => {
    return pendingChangesRef.current.size;
  };

  const getPendingChangesForPopup = (): IPendingChange[] => {
    const changes: IPendingChange[] = [];
    
    pendingChangesRef.current.forEach((value, id) => {
      changes.push({
        id,
        driverName: `${value.original.waybill?.driver?.name || 'Não informado'} ${value.original.waybill?.driver?.surname || ''}`.trim(),
        licensePlate: value.original.waybill?.vehicle?.licensePlate || 'N/A',
        deliveryDate: value.original.waybill?.deliveryDate 
          ? dayjs(value.original.waybill.deliveryDate).format('DD/MM/YYYY') 
          : undefined,
        route: value.original.waybill?.route?.toString() || undefined,
        originalCostAdjustment: value.original.costAdjustment,
        newCostAdjustment: value.edited.costAdjustment,
        originalDefaultCost: value.original.defaultCost,
        newDefaultCost: value.edited.defaultCost,
        originalUnloadingCost: value.original.unloadingCost,
        newUnloadingCost: value.edited.unloadingCost,
        originalLeaderCommission: value.original.leaderCommission,
        newLeaderCommission: value.edited.leaderCommission,
      });
    });

    return changes;
  };

  const handleSaveClick = () => {
    if (getPendingChangesCount() === 0) return;
    setConfirmPopupOpen(true);
  };

  const handleSaveBatch = async () => {
    const changes = Array.from(pendingChangesRef.current.values());
    if (changes.length === 0) return;
    
    try {
      setLoading(true);
      await costManagementService.updateLastMileCostBatch({
        costs: changes.map(({ original, edited }) => ({
          id: original.id,
          waybill_id: original.waybill.id,
          default_cost: edited.defaultCost,
          cost_adjustment: edited.costAdjustment,
          unloading_cost: edited.unloadingCost ?? null,
          leader_commission: edited.leaderCommission,
        }))
      });
      
      toast.success('Alterações salvas com sucesso!');
      
      // Limpar alterações pendentes
      pendingChangesRef.current.clear();
      setConfirmPopupOpen(false);
      
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao salvar alterações";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeParams = (newParamsOrFn: Function | typeof params) => {
    const updatedParams = typeof newParamsOrFn === 'function' ? newParamsOrFn(params) : newParamsOrFn;
    setParams(updatedParams);

    const newSearchParams = new URLSearchParams();
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => newSearchParams.append(key, v.toString()));
      } else if (value) {
        newSearchParams.set(key, value.toString());
      }
    });
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page: number) => {
    handleChangeParams({ ...params, page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    // Ao mudar pageSize, reseta para página 1
    handleChangeParams({ ...params, page: 1, pageSize });
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  // Mapeador de filtros para ActiveFilters
  const filterMapper = {
    startDate: {
      label: "Data início",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
    },
    endDate: {
      label: "Data fim",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate,
    },
    transshipPointIds: {
      label: "Centros de Distribuição",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.transshipPointIds || params.transshipPointIds.length === 0,
    },
    teamIds: {
      label: "Times",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.teamIds || params.teamIds.length === 0,
    }
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newParams = { ...params };

    if (filterKey in newParams) {
      if (filterKey === 'transshipPointIds' || filterKey === 'teamIds') {
        (newParams as any)[filterKey] = [];
      } else if (filterKey === 'startDate' || filterKey === 'endDate') {
        const today = dateToday();
        (newParams as any)['startDate'] = today;
        (newParams as any)['endDate'] = today;
      } else if (filterKey === 'search') {
        (newParams as any)[filterKey] = '';
      }

      handleChangeParams(newParams);
    }
  };

  const handleClearAllFilters = () => {
    const today = dateToday();
    const clearedParams = {
      startDate: today,
      endDate: today,
      transshipPointIds: [],
      teamIds: [],
      search: '',
      page: 1,
      pageSize: 15,
    };

    handleChangeParams(clearedParams);
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      const exportParams = {
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search,
        transshipPointIds: params.transshipPointIds,
        teamIds: params.teamIds,
      };
      const response = await costManagementService.exportCosts('last_mile', exportParams);
      const blob = new Blob([response], { type: 'application/ms-excel;charset=utf-8' });
      saveAs(blob, 'Last-Mile-Custos.xlsx');
    } catch (e) {
      toast.error("Houve um erro ao tentar baixar a planilha");
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = getPendingChangesCount();

  return (
    <div className="pageContent">
      <PageContainer>
        <MainContainer>
          <div className='infos'>
            <h3>Last Mile</h3>
            <Toolbar
              params={params}
              paramState={handleChangeParams}
              loadingState={loading}
              placeholder="Motorista ou placa"
              alignButtonsToEnd={true}
              page='last-mile-costs'
              extraButtons={[
                {
                  text: 'Exportar planilha',
                  onClick: downloadFile,
                  disableState: loading,
                  type: 'outlined',
                  icon: <img src={PlanilhaIcon} alt="Exportar planilha" />
                },
                {
                  text: pendingCount > 0 ? `Salvar alterações (${pendingCount})` : 'Salvar alterações',
                  onClick: handleSaveClick,
                  disableState: pendingCount === 0 || loading,
                  type: 'filled'
                }
              ]}
            />
          </div>

          <ActiveFilters
            params={params}
            mapper={filterMapper}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            title="Filtros aplicados"
            hideWhenEmpty={true}
          />

          <DataTable loading={loading} columns={defaultColumns} contentHeight="40vh" loadingType="inline">
            {editedData.map((element) => (
              <FormTableLine
                key={element.id}
                element={element}
                onEditField={(id, field, value) => handleEditField(id, field as keyof ILastMile, value)}
                onRollback={handleRollback}
                isEdited={isRowEdited(element.id)}
              />
            ))}
          </DataTable>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <CustomPagination
              currentPage={paginationData.pageIndex}
              totalPages={paginationData.totalPages}
              onPageChange={handlePageChange}
              pageSize={params.pageSize}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={true}
            />
          </Box>

          <div className="spacer" />
        </MainContainer>
      </PageContainer>

      <ConfirmChangesPopup
        open={confirmPopupOpen}
        onClose={() => setConfirmPopupOpen(false)}
        onConfirm={handleSaveBatch}
        changes={getPendingChangesForPopup()}
        loading={loading}
      />
    </div>
  )
}
